import React from 'react';
import PropTypes from 'prop-types';
import round from '../../../helpers/round';
import terzaghiPeckMesriGraph from '../../../assets/calculations/piPhi/Terzaghi_Peck_Mesri_1996.JPG';

const GRAPH_WIDTH = 380;
const IMAGE = { width: 1113, height: 502 };
const PLOT = {
  left: 98,
  right: 1060,
  top: 30,
  bottom: 431,
  xBreakAt100: 758,
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const mapPiToX = (pi) => {
  if (pi <= 100) {
    const normalized = clamp(pi, 0, 100) / 100;
    return PLOT.left + normalized * (PLOT.xBreakAt100 - PLOT.left);
  }
  const minLog = Math.log10(100);
  const maxLog = Math.log10(1000);
  const normalized = (clamp(Math.log10(pi), minLog, maxLog) - minLog) / (maxLog - minLog);
  return PLOT.xBreakAt100 + normalized * (PLOT.right - PLOT.xBreakAt100);
};

const mapPhiToY = (phi) => {
  const normalized = clamp(phi, 0, 50) / 50;
  return PLOT.bottom - normalized * (PLOT.bottom - PLOT.top);
};

const buildGraph = (pi, phi) => {
  const height = (GRAPH_WIDTH * IMAGE.height) / IMAGE.width;
  const scale = GRAPH_WIDTH / IMAGE.width;
  const x = mapPiToX(pi) * scale;
  const y = mapPhiToY(phi) * scale;
  const left = PLOT.left * scale;
  const bottom = PLOT.bottom * scale;

  return {
    width: GRAPH_WIDTH,
    height,
    x,
    y,
    left,
    bottom,
    labelX: left + 4,
    labelY: y - 6,
  };
};

const ClayTpmGraphSvg = ({ pi, phi, showOverlay }) => {
  const piValue = Number(pi);
  const phiValue = Number(phi);
  const graph = buildGraph(piValue, phiValue);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${graph.width} ${graph.height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Terzaghi, Peck, Mesri PI versus effective friction angle graph"
      style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: `${graph.width} / ${graph.height}` }}
    >
      <image href={terzaghiPeckMesriGraph} x="0" y="0" width={graph.width} height={graph.height} />
      {showOverlay && (
        <g stroke="#b33a3a" fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke">
          <line x1={graph.x} y1={graph.y} x2={graph.x} y2={graph.bottom} />
          <polyline points={`${graph.x - 3},${graph.y + 5} ${graph.x},${graph.y} ${graph.x + 3},${graph.y + 5}`} />
          <line x1={graph.left} y1={graph.y} x2={graph.x} y2={graph.y} />
          <polyline
            points={`${graph.left + 6},${graph.y - 3} ${graph.left + 1},${graph.y} ${graph.left + 6},${graph.y + 3}`}
          />
        </g>
      )}
      {showOverlay && (
        <text x={graph.labelX} y={graph.labelY} fill="#b33a3a" fontSize="10" fontWeight="700">
          φ&apos;={round(phiValue, 2)} deg
        </text>
      )}
    </svg>
  );
};

ClayTpmGraphSvg.propTypes = {
  pi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  phi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  showOverlay: PropTypes.bool,
};

ClayTpmGraphSvg.defaultProps = {
  showOverlay: false,
};

export default React.memo(ClayTpmGraphSvg);
